import { z } from 'zod';

/**
 * Zod schemas for the `options.chartConfig` block of the two chart widgets.
 *
 * These are config keys on live displays and therefore immovable (CLAUDE.md constraint 1),
 * misspellings included — `grafLegend` and `title.titletText` are both [sic]. Everything is
 * optional: the legacy components read each key defensively and fell back per key, and a chart
 * widget whose `chartConfig` is half-filled has to render, not disappear.
 *
 * The *defaults* deliberately do not live here. The legacy code mixes two fallback tests —
 * `x !== undefined ? x : d` for some keys and `x ? x : d` (truthiness) for others — and which
 * one applies is per key, not per schema. Encoding a `.default()` here would quietly convert
 * every truthiness fallback into an `undefined` fallback, so a configured `0` would start
 * meaning `0` where today it means "use the default". The components apply the legacy test.
 */

/** A colour list, or the single colour a hand-edited config might carry instead. */
const colorValueSchema = z.union([z.string(), z.array(z.string())]);

/* ------------------------------------- Doughnut ----------------------------------------- */

/** `chartConfig.chartText` — the label painted in the middle of the ring. */
export const doughnutMiddleTextSchema = z.object({
  /** rem [sic] — canvas rejects the unit, see W-6 in `DoughnutChart.tsx`. */
  fontSize: z.number().optional(),
  fontFamily: z.string().optional(),
  /** Spelled `color`, not `fontColor`, unlike every other font block in the app. */
  color: z.string().optional(),
});

export const doughnutChartConfigSchema = z.object({
  /** rem. Applied to the canvas as both `height` and `max-height`. */
  maxHeight: z.number().optional(),
  labels: z.array(z.string()).optional(),
  /** Per-segment fill colours. Also passed to `legend.labels.color`, exactly as legacy did. */
  labelColors: z.array(z.string()).optional(),
  /** `'80%'` in every deployed config. Chart.js also accepts a number of pixels. */
  cutout: z.union([z.string(), z.number()]).optional(),
  borderRadius: z.number().optional(),
  chartBorderColor: colorValueSchema.optional(),
  rotation: z.number().optional(),
  circumference: z.number().optional(),
  /** [sic] "graf". Maps to `plugins.legend.display`. */
  grafLegend: z.boolean().optional(),
  /** `plugins.datalabels.color` — only reachable when `chartText` is absent (§6). */
  chartLabelColor: z.string().optional(),
  chartText: doughnutMiddleTextSchema.optional(),
});

export type DoughnutChartConfig = z.infer<typeof doughnutChartConfigSchema>;

/* --------------------------------------- Bar -------------------------------------------- */

/** `chartConfig.title` — Chart.js's own title plugin, above the plot area. */
const barTitleSchema = z.object({
  /** [sic] "titlet". Presence of a non-empty value is what turns the title on. */
  titletText: z.string().optional(),
  /** rem, converted to px with `remToPixels`. */
  fontSize: z.number().optional(),
  fontFamily: z.string().optional(),
  fontWeight: z.string().optional(),
  fontColor: z.string().optional(),
});

const barTickFontSchema = z.object({
  /** rem, converted to px. Note the key is `size`, not `fontSize`, on this block only. */
  size: z.number().optional(),
  family: z.string().optional(),
  weight: z.string().optional(),
});

const barAxisSchema = z.object({
  /** Gridlines. */
  display: z.boolean().optional(),
  color: z.string().optional(),
  /**
   * [chart.js 4] Chart.js 3's `scales.<id>.grid.drawBorder`. Removed in 4 in favour of
   * `scales.<id>.border.display`; `BarChart` maps it across. Left under its legacy name here
   * because that is what the deployed configs carry.
   */
  drawBorder: z.boolean().optional(),
  ticks: z
    .object({
      display: z.boolean().optional(),
      color: z.string().optional(),
      font: barTickFontSchema.optional(),
    })
    .prefault({}),
  /** `y` only. */
  suggestedMax: z.number().optional(),
});

/** `chartConfig.horizontalLine` — the safety-line threshold and its label box. */
const horizontalLineSchema = z.object({
  /**
   * Optional, deliberately. Legacy guarded it with `!== undefined` and simply drew nothing
   * without it; making it required here would fail the whole `chartConfig` parse and blank the
   * chart instead of the line.
   */
  lineAt: z.number().optional(),
  lineColor: z.string().nullable().default(null),
  label: z
    .object({
      borderRadius: z.number().optional(),
      labelText: z.string().nullable().default(null),
      /** rem [sic] — rejected by canvas, see W-6. */
      fontSize: z.number().nullable().default(null),
      fontColor: z.string().nullable().default(null),
      fontFamily: z.string().nullable().default(null),
      borderWidth: z.number().nullable().default(null),
      textOffset: z.number().nullable().default(null),
      borderColor: z.string().nullable().default(null),
      backgroundColor: z.string().nullable().default(null),
    })
    .nullable()
    .optional(),
});

/** `chartConfig.barTickOption` — bar cap height, gradient fill and the value-label font. */
const barTickOptionSchema = z.object({
  display: z.boolean().optional(),
  fontFamily: z.string().optional(),
  /** rem [sic] — rejected by canvas, see W-6. */
  fontSize: z.number().optional(),
  fontColor: z.string().optional(),
  gradientFrom: z.string().optional(),
  gradientTo: z.string().optional(),
  /** Height of the peaked cap, in pixels. */
  shapeHeight: z.number().optional(),
});

export const barChartConfigSchema = z.object({
  /** rem. Applied to the canvas as both `height` and `max-height`. */
  maxHeight: z.number().optional(),
  /**
   * One label per bar. Also the source of the widget's initial data — one `0` per label —
   * which is why it defaults to an empty list rather than being required: a chart widget with
   * a malformed `labels` must render an empty plot, not vanish.
   */
  labels: z.array(z.string()).prefault([]),
  backgroundColor: colorValueSchema.optional(),
  borderColor: colorValueSchema.optional(),
  title: barTitleSchema.optional(),
  horizontalLine: horizontalLineSchema.optional(),
  barTickOption: barTickOptionSchema.optional(),
  grid: z
    .object({
      x: barAxisSchema.optional(),
      y: barAxisSchema.optional(),
    })
    .optional(),
});

export type BarChartConfig = z.infer<typeof barChartConfigSchema>;
